import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { Text } from '@rsos/base-components';
import { UNITE_EVENT_PANE } from '@rsos/capstone/src/constants/capabilities';
import { UNITE_EVENT_PANE_PERMISSION } from '@rsos/capstone/src/constants/permissionNames';
import { AGENCY_CHAT, EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import useHasCapability from '@rsos/hooks/useHasCapability';
import useHasPermission from '@rsos/hooks/useHasPermission';
import {
  removeAllNonCallEvents,
  resetSelectedAirAmbulance,
} from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import {
  removeAllCalls,
  viewAllCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  AVIVE_APP_LAUNCHER_MAP_CARD,
  MEDIA_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { hide as hideRade } from '../../JurisdictionView/RADECard/radeSlice';
import {
  Container,
  Tooltip,
  TooltipChild,
  Subtitle,
  ButtonWrapper,
  ViewJurisdictionButton,
  StyledHomeIcon,
  ClearAllButton,
  StyledArchiveIcon,
  Wrapper,
} from './AllEvents.styles';
import RemoveAllEventsModal from './RemoveAllEventsModal';

const TOOLTIP_TEXT = 'Unavailable when viewing an incident';

/**
 *
 * @param {string} selectedCallerID - the selected caller id
 * @param {string} activeQueueTab - the selected tab (calls/alerts)
 * @param {boolean} hasCalls - if length of calls > 0
 *
 */
const AllEvents = ({
  hasCalls,
  activeQueueTab,
  selectedCallerID,
  onViewJurisdiction,
}) => {
  const hasUniteEventPaneCapabilities = useHasCapability(UNITE_EVENT_PANE);

  const hasUniteEventPanePermission = useHasPermission(
    UNITE_EVENT_PANE_PERMISSION,
  );

  const hasAirAmbulanceAccess = useHasAccessToAirAmbulance();

  const selectedAirAmbulance = useSelector(
    state => state?.airAmbulance?.selectedAirAmbulance,
  );
  const airAmbulanceNonCalls = useSelector(
    state => state?.airAmbulance?.requests?.nonCalls,
  );

  const isUniteEventPaneEnabled = Boolean(
    hasUniteEventPaneCapabilities?.hasCapability &&
      hasUniteEventPanePermission?.hasPermission,
  );

  const hasNonCallAirAmbulances = useMemo(() => {
    return (
      Object.keys(airAmbulanceNonCalls || {}).length &&
      hasAirAmbulanceAccess &&
      isUniteEventPaneEnabled
    );
  }, [airAmbulanceNonCalls, hasAirAmbulanceAccess, isUniteEventPaneEnabled]);

  const dispatch = useDispatch();
  const { display_name: psapDisplayName } = useSelector(
    state => state.psaps.currentPSAP,
  );
  const { irpVersion } = useSelector(state => state.sinatra.irp);
  const { conversations: rcsConversations } = useSelector(state => state.rcs);
  const [, setRemoveAllVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleViewAll = () => {
    dispatch(viewAllCalls());
    dispatch(resetSelectedAirAmbulance());
    dispatch(hideRade());
    onViewJurisdiction();
    gspx.trackEvent({
      name: 'click_on_all_calls',
      type: GPX_EVENT_TYPES.INFO,
      feature: GPX_FEATURES.LEGACY,
      capability: GPX_CAPABILITIES.LEGACY,
      route: GPX_ROUTES.CALL_QUEUE,
      additionalKeys: {
        legacy_category: 'Data Jurisdiction',
        legacy_name: 'Click on All Calls',
        Category: 'Jurisdiction View',
        'Launched date': new Date(),
        irpVersion,
        serviceType: '',
        classOfServices: null,
      },
    });
    dispatch(closeCard(AVIVE_APP_LAUNCHER_MAP_CARD));
    dispatch(closeCard(MEDIA_CARD));
  };

  const handleRightClick = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRemoveAllEvents = () => {
    gspx.trackEvent({
      name: 'clicks_remove_all_calls_modal',
      type: GPX_EVENT_TYPES.INFO,
      feature: GPX_FEATURES.LEGACY,
      capability: GPX_CAPABILITIES.LEGACY,
      route: GPX_ROUTES.CALL_QUEUE,
      additionalKeys: {
        legacy_category: 'Data Jurisdiction',
        legacy_name: 'Clicks on remove from remove all calls modal',
        Category: 'Jurisdiction View',
        'Launched date': new Date(),
        irpVersion,
      },
    });
    if (activeQueueTab === EMERGENCY_CALLS) {
      dispatch(removeAllCalls());
      dispatch(removeAllNonCallEvents());
    } else {
      // next phase remove all alerts
      // dispatch(removeAllAlerts());
    }
    setRemoveAllVisible(false);
    setIsModalVisible(false);

    // dismiss all rcs toasts
    if (rcsConversations) {
      const callerIds = Object.keys(rcsConversations);
      callerIds.forEach(id => {
        toast.dismiss(`toastRcsAvailable-${id}`);
      });
    }
  };

  const handleClearAllClick = () => {
    if (
      activeQueueTab === EMERGENCY_CALLS &&
      !selectedCallerID &&
      !selectedAirAmbulance &&
      (hasCalls || hasNonCallAirAmbulances)
    ) {
      setIsModalVisible(true);
    }
  };

  if (activeQueueTab === AGENCY_CHAT) return null;

  return (
    <>
      <Container onContextMenu={handleRightClick}>
        <Wrapper data-name="all-911-events">
          {psapDisplayName && <Subtitle>{psapDisplayName}</Subtitle>}
          <ButtonWrapper>
            <ViewJurisdictionButton onClick={handleViewAll}>
              <StyledHomeIcon /> View Jurisdiction
            </ViewJurisdictionButton>
            <Tooltip>
              {(selectedCallerID || selectedAirAmbulance) && (
                <TooltipChild data-name="clear-all-tooltip">
                  <Text size="small">{TOOLTIP_TEXT}</Text>
                </TooltipChild>
              )}
              <ClearAllButton
                onClick={handleClearAllClick}
                dataName="clear-all"
                disabled={
                  activeQueueTab !== EMERGENCY_CALLS ||
                  (!hasCalls && !hasNonCallAirAmbulances) ||
                  selectedCallerID ||
                  selectedAirAmbulance
                }
              >
                <StyledArchiveIcon /> Clear All Events
              </ClearAllButton>
            </Tooltip>
          </ButtonWrapper>
        </Wrapper>
      </Container>
      <RemoveAllEventsModal
        activeQueueTab={activeQueueTab}
        isOpened={isModalVisible}
        onConfirm={handleRemoveAllEvents}
        onClose={() => setIsModalVisible(false)}
      />
    </>
  );
};

AllEvents.propTypes = {
  selectedCallerID: PropTypes.string,
};

export default AllEvents;
